<template>
    <div class="gallery-shift-grid">
        <button
                v-for="(file, fileIndex) in galleryFiles"
                :key="file.key"
                type="button"
                class="gallery-shift-image"
                :class="{
                    'gallery-shift-image--video': file.isVideo,
                    'gallery-shift-image--file': !file.isImage && !file.isVideo,
                    'gallery-shift-image--annulled': annulled
                }"
                @click="openViewer(fileIndex)"
        >
            <gallery-thumbnail
                    v-if="file.isImage"
                    :ref="'thumbnail-' + fileIndex"
                    :src="file.thumbnailUrl"
                    :name="file.name"
            />
            <span v-else class="gallery-shift-file-preview">
                <i :class="file.iconClass"></i>
                <span v-if="file.isVideo" class="gallery-shift-video-play">
                    <i class="fas fa-play"></i>
                </span>
                <small>{{ file.formatLabel }}</small>
            </span>
            <span v-if="annulled" class="gallery-shift-annulled-mark">
                ОТЧЁТ АННУЛИРОВАН
            </span>
        </button>
    </div>
</template>

<script>
import GalleryThumbnail from './GalleryThumbnail.vue';
const VIDEO_EXTENSIONS = ['mp4', 'mov', 'm4v', 'avi', 'webm', 'mkv', '3gp'];
const NOTICE_TYPES = {
    START: `start`,
    BETWEEN: `between`,
    END: `end`,
    LOADED_END: `loaded-end`
};

function getShiftId(shift, fallback) {
    return shift && (shift.shiftId || shift.shift_id)
        ? (shift.shiftId || shift.shift_id)
        : fallback;
}

function getShiftDateTime(shift) {
    return shift && (shift.dateTime || shift.date_time_begin)
        ? (shift.dateTime || shift.date_time_begin)
        : null;
}

function encodeSvgDataUrl(svg) {
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

const NOTICE_SLIDE_URL = encodeSvgDataUrl([
    `<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="800" viewBox="0 0 1200 800">`,
    `<rect width="1200" height="800" fill="#07111f"/>`,
    `</svg>`
].join(``));

const NOTICE_THUMBNAIL_URL = encodeSvgDataUrl([
    `<svg xmlns="http://www.w3.org/2000/svg" width="160" height="120" viewBox="0 0 160 120">`,
    `<rect width="160" height="120" fill="#102338"/>`,
    `<rect x="7" y="7" width="146" height="106" rx="10" fill="none" stroke="#ffc107" stroke-width="5"/>`,
    `<circle cx="80" cy="45" r="15" fill="#ffc107"/>`,
    `<text x="80" y="51" text-anchor="middle" font-family="Arial, sans-serif" font-size="22" font-weight="700" fill="#07111f">i</text>`,
    `<text x="80" y="86" text-anchor="middle" font-family="Arial, sans-serif" font-size="15" font-weight="700" fill="#ffffff">Смена</text>`,
    `</svg>`
].join(``));

export default {
    name: "Images",
    components: {GalleryThumbnail},
    computed: {
        galleryFiles() {
            return (this.images || []).map((item, index) => this.normalizeGalleryFile(item, index));
        },
        original() {
            return this.galleryFiles.map(file => file.url);
        },
        viewerShifts() {
            const source = Array.isArray(this.shifts) && this.shifts.length
                ? this.shifts
                : [{
                    shiftId: this.shiftId,
                    files: this.images || [],
                    isAnnulled: this.annulled,
                    annulmentReason: this.annulmentReason
                }];

            return source.filter(shift => {
                return shift && Array.isArray(shift.files) && shift.files.length;
            });
        },
        viewerFiles() {
            const shifts = this.viewerShifts;
            if (!shifts.length) {
                return this.galleryFiles.map(file => this.toViewerFile(file, null, 0));
            }

            return shifts.reduce((result, shift, index) => {
                if (index === 0) {
                    result.push(this.buildBoundaryNotice(NOTICE_TYPES.START, null, shift));
                }

                this.normalizedShiftFiles(shift).forEach(file => {
                    result.push(this.toViewerFile(file, shift, index));
                });

                if (index < shifts.length - 1) {
                    result.push(this.buildBoundaryNotice(NOTICE_TYPES.BETWEEN, shift, shifts[index + 1]));
                } else {
                    result.push(this.buildBoundaryNotice(
                        this.shiftsCompleted ? NOTICE_TYPES.END : NOTICE_TYPES.LOADED_END,
                        shift,
                        null
                    ));
                }

                return result;
            }, []);
        },
        authQuery() {
            const token = this.$store && this.$store.state ? this.$store.state.token : null;
            return token ? `?gjuid=${encodeURIComponent(token)}` : ``;
        }
    },
    methods: {
        getShiftTitle(shift) {
            if (!shift) {
                return `Смена`;
            }

            const shiftId = getShiftId(shift, this.shiftId);
            const dateTime = getShiftDateTime(shift);
            const date = dateTime ? ` от ${this.$moment(dateTime).format("DD.MM.YYYY dddd HH:mm")}` : ``;
            const title = `Смена ${shiftId}${date}`;
            return shift && shift.isAnnulled ? `⛔ ОТЧЁТ АННУЛИРОВАН — ${title}` : title;
        },
        normalizedShiftFiles(shift) {
            const shiftId = getShiftId(shift, this.shiftId);
            const files = shift && Array.isArray(shift.files) ? shift.files : [];
            return files.map((item, index) => this.normalizeGalleryFile(item, index, shiftId));
        },
        toViewerFile(file, shift, shiftIndex) {
            return {
                id: file.id,
                key: file.key,
                name: file.name,
                url: file.url,
                downloadUrl: file.url,
                thumbnailUrl: file.thumbnailUrl,
                mimeType: file.mimeType,
                isImage: file.isImage,
                isVideo: file.isVideo,
                galleryShiftId: getShiftId(shift, this.shiftId),
                galleryShiftIndex: shiftIndex,
                galleryShiftTitle: this.getShiftTitle(shift),
                reportAnnulled: Boolean(shift && shift.isAnnulled),
                reportAnnulmentReason: shift && shift.annulmentReason || ``,
                raw: file.raw
            };
        },
        buildBoundaryNotice(type, previousShift, nextShift) {
            const previousTitle = this.getShiftTitle(previousShift);
            const nextTitle = this.getShiftTitle(nextShift);
            const previousId = previousShift ? getShiftId(previousShift, null) : NOTICE_TYPES.START;
            const nextId = nextShift ? getShiftId(nextShift, null) : NOTICE_TYPES.END;
            const title = this.getBoundaryTitle(type);

            return {
                id: `gallery_notice_${type}_${previousId}_${nextId}`,
                key: `gallery_notice_${type}_${previousId}_${nextId}`,
                name: title,
                url: NOTICE_SLIDE_URL,
                downloadUrl: NOTICE_SLIDE_URL,
                thumbnailUrl: NOTICE_THUMBNAIL_URL,
                placeholderUrl: NOTICE_SLIDE_URL,
                isImage: false,
                isVideo: false,
                isViewerNotice: true,
                viewerNotice: this.getBoundaryNotice(type, previousTitle, nextTitle)
            };
        },
        getBoundaryTitle(type) {
            if (type === NOTICE_TYPES.START) {
                return `Первая смена объекта`;
            }
            if (type === NOTICE_TYPES.END) {
                return `Последняя смена объекта`;
            }
            if (type === NOTICE_TYPES.LOADED_END) {
                return `Последняя загруженная смена`;
            }
            return `Граница смены`;
        },
        getBoundaryNotice(type, previousTitle, nextTitle) {
            if (type === NOTICE_TYPES.START) {
                const notice = {
                    title: `Это первая смена объекта`,
                    body: `Предыдущей смены нет. Нажмите вправо, чтобы продолжить смотреть текущую смену.`,
                    leftText: `Предыдущей смены нет`,
                    rightText: `Вправо - текущая смена`
                };
                return {
                    type,
                    defaultDirection: `backward`,
                    forward: notice,
                    backward: notice
                };
            }

            if (type === NOTICE_TYPES.END) {
                const notice = {
                    title: `Фото по смене закончились`,
                    body: `${previousTitle}. Это последняя смена объекта, следующей смены нет. Нажмите влево, чтобы вернуться к текущей смене.`,
                    leftText: `Влево - текущая смена`,
                    rightText: `Следующей смены нет`
                };
                return {
                    type,
                    defaultDirection: `forward`,
                    forward: notice,
                    backward: notice
                };
            }

            if (type === NOTICE_TYPES.LOADED_END) {
                const notice = {
                    title: `Фото по смене закончились`,
                    body: `${previousTitle}. Следующие смены ещё не загружены в просмотрщик. Нажмите влево, чтобы вернуться к текущей смене.`,
                    leftText: `Влево - текущая смена`,
                    rightText: `Дальше в просмотрщике пока нет смен`
                };
                return {
                    type,
                    defaultDirection: `forward`,
                    forward: notice,
                    backward: notice
                };
            }

            return {
                type,
                defaultDirection: `forward`,
                forward: {
                    title: `Фото по этой смене закончились`,
                    body: `${previousTitle}. Нажмите вправо, чтобы перейти к следующей смене: ${nextTitle}. Нажмите влево, чтобы продолжить смотреть текущую смену.`,
                    leftText: `Влево - текущая смена`,
                    rightText: `Вправо - следующая смена`
                },
                backward: {
                    title: `Начало смены`,
                    body: `${nextTitle}. Нажмите влево, чтобы перейти к предыдущей смене: ${previousTitle}. Нажмите вправо, чтобы продолжить смотреть текущую смену.`,
                    leftText: `Влево - предыдущая смена`,
                    rightText: `Вправо - текущая смена`
                }
            };
        },
        normalizeGalleryFile(item, index, shiftId = this.shiftId) {
            const isObject = item && typeof item === 'object';
            const id = isObject ? item.id : item;
            const name = isObject
                ? (item.name || item.filename || `Файл ${id}`)
                : `Фото ${id}`;
            const mimeType = String(isObject ? (item.mimeType || item.mime_type || '') : '').toLowerCase();
            const extension = this.getExtension(name);
            const isVideo = Boolean(
                isObject && (item.isVideo || item.is_video || item.type === 'video')
            ) || mimeType.indexOf('video/') === 0 || VIDEO_EXTENSIONS.includes(extension);
            const isImage = !isVideo && (
                !isObject ||
                item.isImage ||
                item.is_image ||
                item.type === 'image' ||
                mimeType.indexOf('image/') === 0
            );
            const url = `/images/${id}${this.authQuery}`;
            const thumbnailUrl = isImage ? `/images/mini/${id}${this.authQuery}` : null;

            return {
                id: `${shiftId}_${id || index}`,
                key: `${shiftId}_${id || index}_${isVideo ? 'video' : 'image'}`,
                name,
                url,
                thumbnailUrl,
                mimeType,
                isImage,
                isVideo,
                formatLabel: isVideo ? 'VIDEO' : (extension ? extension.toUpperCase() : 'FILE'),
                iconClass: isVideo ? 'fas fa-file-video' : 'fas fa-file',
                raw: item
            };
        },
        getExtension(name) {
            const match = String(name || '').toLowerCase().match(/\.([a-z0-9]{1,8})$/);
            return match ? match[1] : '';
        },
        isCurrentShiftLastLoaded() {
            if (!Array.isArray(this.shifts) || !this.shifts.length) {
                return false;
            }
            const currentIndex = this.shifts.findIndex(shift => {
                return String(getShiftId(shift, null)) === String(this.shiftId);
            });
            const index = currentIndex >= 0 ? currentIndex : Number(this.shiftIndex);
            return Number.isInteger(index) && index === this.shifts.length - 1;
        },
        async ensureNextShiftLoadedForViewer() {
            if (
                !this.isCurrentShiftLastLoaded()
                || this.shiftsCompleted
                || typeof this.loadMoreShifts !== `function`
            ) {
                return;
            }

            try {
                await this.loadMoreShifts();
            } catch (err) {
                return null;
            }
        },
        getViewerStartIndex(index, files) {
            const target = this.galleryFiles[index];
            if (target) {
                const targetIndex = files.findIndex(file => String(file.id) === String(target.id));
                if (targetIndex >= 0) {
                    return targetIndex;
                }
            }

            const firstMediaIndex = files.findIndex(file => !file.isViewerNotice);
            return firstMediaIndex >= 0 ? firstMediaIndex : 0;
        },
        async openViewer(index) {
            const reference = this.$refs['thumbnail-' + index];
            const thumbnail = Array.isArray(reference) ? reference[0] : reference;
            if (thumbnail && thumbnail.retryIfFailed()) return;
            if (!this.$fileViewer) {
                window.open(this.original[index], `_blank`);
                return;
            }
            await this.ensureNextShiftLoadedForViewer();
            if (this._isBeingDestroyed || this._isDestroyed) return;
            const files = this.viewerFiles;
            this.$fileViewer.open(files, {
                startIndex: this.getViewerStartIndex(index, files),
                source: `gallery`,
                continuous: false
            });
        }
    },
    props: {
        shiftId: {
            type: Number,
            default: null
        },
        images: {
            type: Array,
            default: () => []
        },
        shifts: {
            type: Array,
            default: () => []
        },
        shiftIndex: {
            type: Number,
            default: 0
        },
        shiftsCompleted: {
            type: Boolean,
            default: false
        },
        loadMoreShifts: {
            type: Function,
            default: null
        },
        annulled: {
            type: Boolean,
            default: false
        },
        annulmentReason: {
            type: String,
            default: ``
        }
    }
}
</script>

<style scoped>
.gallery-shift-image {
    appearance: none;
    background: transparent;
    border: 0;
    padding: 0;
    cursor: pointer;
    aspect-ratio: 1 / 1;
    border-radius: 4px;
    display: block;
    height: 100%;
    overflow: hidden;
    position: relative;
    text-align: center;
    object-fit: cover;
    width: 100%;
}

.gallery-shift-image img {
    display: block;
    height: 100%;
    object-fit: cover;
    width: 100%;
}

.gallery-shift-image--annulled::after {
    background: rgba(127, 16, 27, .22);
    content: "";
    inset: 0;
    pointer-events: none;
    position: absolute;
}

.gallery-shift-annulled-mark {
    background: rgba(127, 16, 27, .94);
    bottom: 0;
    color: #fff;
    font-size: 10px;
    font-weight: 900;
    left: 0;
    letter-spacing: .02em;
    line-height: 1.2;
    padding: 5px 3px;
    pointer-events: none;
    position: absolute;
    right: 0;
    z-index: 1;
}

.gallery-shift-file-preview {
    align-items: center;
    background: #1f2933;
    color: #fff;
    display: flex;
    flex-direction: column;
    gap: 8px;
    height: 100%;
    justify-content: center;
    position: relative;
    width: 100%;
}

.gallery-shift-file-preview > i {
    font-size: 34px;
    opacity: 0.9;
}

.gallery-shift-video-play {
    align-items: center;
    background: rgba(255, 255, 255, 0.92);
    border-radius: 999px;
    color: #17a2b8;
    display: flex;
    height: 42px;
    justify-content: center;
    left: 50%;
    padding-left: 3px;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 42px;
}

.gallery-shift-file-preview small {
    bottom: 8px;
    font-weight: 700;
    left: 0;
    letter-spacing: 0;
    position: absolute;
    right: 0;
}

.gallery-shift-grid {
    direction: ltr;
    display: grid;
    gap: 8px;
    grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
    padding: 8px;
}

@media (max-width: 767.98px) {
    .gallery-shift-grid {
        gap: 4px;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        padding: 4px;
    }
}

@media (max-width: 359.98px) {
    .gallery-shift-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
</style>
